import React, {
  type FunctionComponent,
  type VoidFunctionComponent,
} from "react"

export type NavTheme = "light" | "dark"

interface HeaderConfig {
  navTheme: NavTheme
  nonDesktopBackPath?: string
  visible: boolean
  logoLinkHref?: string
}

const defaultHeaderConfig: HeaderConfig = {
  navTheme: "light",
  visible: true,
}

interface HeaderConfigContext {
  // Return the 'final' config at the current point in time
  getConfig: () => HeaderConfig
  // Store a new piece of config when a <HeaderConfig> component mounts. Returns
  // a function to remove the same config when the <HeaderConfig> unmounts
  storeConfigItem: (c: Partial<HeaderConfig>) => () => void
}

/**
 * The React context object itself. The fields are set to empty values here so
 * that React knows the shape of the object, but they are initialised 'properly'
 * by the HeaderConfigProvider.
 */
const HeaderConfigContext = React.createContext<HeaderConfigContext>({
  getConfig: () => defaultHeaderConfig,
  storeConfigItem: () => () => {},
})

/**
 * This component sets up the React context by initialising the context and
 * providing the state that actually holds the config objects. It must be
 * rendered above any <HeaderConfig> components; usually it should be placed
 * near the root of the component tree.
 */
export const HeaderConfigProvider: FunctionComponent<{
  children?: React.ReactNode
}> = ({ children }) => {
  // Store a stack of config objects
  const [headerConfigs, setHeaderConfigs] = React.useState<
    { config: Partial<HeaderConfig> }[]
  >([])

  // Fold the configs into a single object, preferring the most 'recent' (i.e.
  // most nested) values
  const finalHeaderConfig = headerConfigs.reduce(
    (acc, { config }) => ({
      ...acc,
      ...config,
    }),
    defaultHeaderConfig
  )

  return (
    <HeaderConfigContext.Provider
      value={{
        getConfig: () => finalHeaderConfig,
        // When a new <HeaderConfig> is rendered, add its config to the stack
        storeConfigItem: (c: Partial<HeaderConfig>) => {
          // Create a new object to ensure we'll be able to find this piece of
          // config again later when we want to remove it
          const uniqueConfig = { config: c }
          setHeaderConfigs(cs => [...cs, uniqueConfig])
          return () => {
            // When a <HeaderConfig> unmounts, remove its config from the stack
            setHeaderConfigs(cs => cs.filter(c => c !== uniqueConfig))
          }
        },
      }}
    >
      {children}
    </HeaderConfigContext.Provider>
  )
}

/**
 * A hook to get the current config. This is used by the header itself, for
 * example.
 */
export const useHeaderConfig = (): HeaderConfig => {
  const { getConfig } = React.useContext(HeaderConfigContext)
  return getConfig()
}

/**
 * If you are trying to configure the header, this is the component you will
 * normally want to use. It hooks into the global context such that when it is
 * rendered, its piece of config is stored; and when it unmounts, its config is
 * removed again.
 *
 * You can use it like so:
 *
 * <HeaderConfig
 *   config={{
 *     navTheme: "transparent"
 *   }}
 * />
 *
 * You don't need to pass values for every configuration field, only those you
 * care about. Note that `<HeaderConfig>` does not accept children.
 */
export const HeaderConfig: VoidFunctionComponent<{
  config: Partial<HeaderConfig>
}> = ({ config }) => {
  const { storeConfigItem } = React.useContext(HeaderConfigContext)

  React.useEffect(() => {
    const removeConfigItem = storeConfigItem(config)
    return removeConfigItem
  }, [])

  return null
}
